/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Modal } from '../ui/Modal';
import { UserService } from '../../services/user.service';
import { CheckCircle, XCircle } from 'lucide-react';

export const ChangePasswordModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const passwordRules = [
    {
      label: 'At least 6 characters',
      valid: newPassword.length >= 6,
    },
    {
      label: 'Contains a capital letter',
      valid: /[A-Z]/.test(newPassword),
    },
    {
      label: 'Contains a number',
      valid: /[0-9]/.test(newPassword),
    },
  ];

  const handleChangePassword = async () => {
    setHasSubmitted(true);
    const newErrors: any = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    else if (newPassword.length < 6) newErrors.newPassword = 'Must be at least 6 characters';
    if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      await UserService.changePassword({ currentPassword, newPassword });
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message.message || 'Failed to change password';

      if (message.toLowerCase().includes('current password is incorrect')) {
        setErrors((prev: any) => ({
          ...prev,
          currentPassword: message,
        }));
      } else if (message.toLowerCase().includes('new password must be different from the current password')) {
        setErrors((prev: any) => ({
          ...prev,
          newPassword: message,
        }));
      } else {
        setErrors((prev: any) => ({
          ...prev,
          general: message,
        }));
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      setHasSubmitted(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Password">
      <Label className="mt-4 mb-1">Current Password</Label>
      <Input
        type="password"
        value={currentPassword}
        onChange={(e) => {
          setCurrentPassword(e.target.value);
          if (errors.currentPassword) {
            setErrors((prev: any) => ({ ...prev, currentPassword: '' }));
          }
        }}
        placeholder="Current password"
        error={hasSubmitted && !!errors.currentPassword}
      />
      {hasSubmitted && errors.currentPassword && (
        <p className="text-sm text-red-500">{errors.currentPassword}</p>
      )}

      <Label className="mt-4 mb-1">New Password</Label>
      <Input
        type="password"
        value={newPassword}
        onChange={(e) => {
          setNewPassword(e.target.value);
          if (errors.newPassword) {
            setErrors((prev: any) => ({ ...prev, newPassword: '' }));
          }
        }}
        placeholder="New password"
        error={hasSubmitted && !!errors.newPassword}
      />
      {hasSubmitted && errors.newPassword && (
        <p className="text-sm text-red-500">{errors.newPassword}</p>
      )}
      {newPassword !== '' && (
        <ul className="mt-2 space-y-1 text-sm">
          {passwordRules.map((rule, index) => (
            <li
              key={index}
              className={`flex items-center gap-2 ${
                rule.valid ? 'text-green-600' : 'text-red-500'
              }`}
            >
              {rule.valid ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              {rule.label}
            </li>
          ))}
        </ul>
      )}

      <Label className="mt-4 mb-1">Confirm New Password</Label>
      <Input
        type="password"
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          if (errors.confirmPassword) {
            setErrors((prev: any) => ({ ...prev, confirmPassword: '' }));
          }
        }}
        placeholder="Confirm password"
        error={hasSubmitted && !!errors.confirmPassword}
      />
      {hasSubmitted && errors.confirmPassword && (
        <p className="text-sm text-red-500">{errors.confirmPassword}</p>
      )}

      {errors.general && (
        <p className="mt-4 text-sm text-center text-red-500">{errors.general}</p>
      )}

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="secondary" className="px-7 sm:px-10" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleChangePassword} className="px-7 sm:px-10">
          Change
        </Button>
      </div>
    </Modal>
  );
};
